import * as React from 'react'
import type { ConsentOptionsDto } from '@lib/dto/mcp.dto'
import { grantCodeSchema } from '@lib/input/mcp.input'
import type { Uuid } from '@lib/validators/params'
import { SegmentedControl, type SegmentedOption } from '@/components/ui/segmented-control'
import type { IMcpConsentService } from '@/types/McpConsentService.types'
import { ConsentLayout } from './ConsentLayout'
import { GrantCodeField } from './GrantCodeField'
import { useConsentSubmit } from './useConsentSubmit'

type StaffOptions = Extract<ConsentOptionsDto, { as: 'staff' }>
type StaffAccess = 'read' | 'changes'

function staffChoices(offerChanges: boolean): SegmentedOption<StaffAccess>[] {
  const read: SegmentedOption<StaffAccess> = { value: 'read', label: 'Read only' }
  if (!offerChanges) return [read]
  return [read, { value: 'changes', label: 'Make changes', tone: 'warning' }]
}

interface StaffConsentFormProps {
  requestId: Uuid
  options: StaffOptions
  service: IMcpConsentService
  onSignOut: () => void
  onDone: (redirectUrl: string) => void
}

/** What PixelSign staff let an AI app do: read the console, or (admins, recognized apps) make changes too. */
export function StaffConsentForm({ requestId, options, service, onSignOut, onDone }: StaffConsentFormProps) {
  const { pending, errors, setErrors, approve, deny } = useConsentSubmit(requestId, service, onDone)
  const [access, setAccess] = React.useState<StaffAccess>('read')
  const [code, setCode] = React.useState('')
  const changes = access === 'changes'
  const choices = staffChoices(options.offerChanges)

  function submit(): void {
    if (!changes) {
      void approve({ as: 'staff', changes: false })
      return
    }
    const codeParsed = grantCodeSchema.safeParse(code)
    if (!codeParsed.success) {
      setErrors({ code: 'Enter the 6-digit code from the email.' })
      return
    }
    void approve({ as: 'staff', changes: true, code: codeParsed.data })
  }

  return (
    <ConsentLayout
      app={options.app}
      user={options.user}
      pending={pending}
      formError={errors.form}
      onSignOut={onSignOut}
      onApprove={submit}
      onDeny={() => void deny()}
    >
      <div className="space-y-2">
        <h2 className="text-body font-medium">What it can do in the ops console</h2>
        <SegmentedControl ariaLabel="Staff access" value={access} onChange={setAccess} options={choices} fullWidth />
        <p className="text-caption text-muted-foreground">
          {changes
            ? 'Can do what you can in the ops console. PixelSign confirms every change with you.'
            : 'Can look up organizations, envelopes and logs, but can’t change anything.'}
        </p>
        {options.offerChanges ? null : (
          <p className="text-caption text-muted-foreground">
            Only admins connecting a recognized app can let it make changes.
          </p>
        )}
      </div>
      {changes ? (
        <GrantCodeField
          email={options.user.email}
          value={code}
          onChange={setCode}
          onSend={() => service.sendCode(requestId, { as: 'staff' })}
          error={errors.code}
        />
      ) : null}
    </ConsentLayout>
  )
}
